import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { format } from "date-fns";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "@/hooks/use-toast";
import { useOrganization } from "@/contexts/OrganizationContext";
import { useSearchParams } from "react-router-dom";
import ProducerAccountsPayable from "./ProducerAccountsPayable";
import ProducerAccountsReceivable from "./ProducerAccountsReceivable";
import ProducerCashFlow from "./ProducerCashFlow";
import ProducerBankAccounts from "./ProducerBankAccounts";
import ProducerEventReconciliation, { ReconciliationEvent } from "./ProducerEventReconciliation";
import ProducerWithdrawals from "./ProducerWithdrawals";
import { CalendarDays, Landmark, TrendingUp, WalletCards, RefreshCw, Activity, TrendingDown, History, Download } from "lucide-react";
import { getCashFlowSummary, getEventReconciliation } from "@/lib/api-financial";
import { cn, formatBRL } from "@/lib/utils";
import { exportToXLSX, ExcelColumn, translateEventStatus } from "@/lib/xlsx-export";
import { panelEyebrow, panelPage, panelPill, panelTitle } from "@/lib/panel-ui";

const getReconciliationExcelColumns = (t: (key: string) => string): ExcelColumn[] => [
  { key: "title", header: t("financial.event"), width: 30 },
  {
    key: "start_date",
    header: t("financial.eventDate"),
    width: 16,
    format: (v: string) => (v ? new Date(v).toLocaleDateString("pt-BR", { timeZone: "America/Sao_Paulo" }) : "—"),
  },
  {
    key: "status",
    header: t("financial.eventStatus"),
    width: 16,
    format: (v: string, row?: { visibility?: string }) => translateEventStatus(v, row?.visibility),
  },
  { key: "ordersCount", header: t("financial.orders"), width: 12, alignment: "right" },
  { key: "ticketsSold", header: t("financial.ticketsSold"), width: 18, alignment: "right" },
  { key: "ticketsCheckedIn", header: t("financial.checkinsDone"), width: 20, alignment: "right" },
  {
    key: "ticketRevenue",
    header: t("financial.ticketRevenue"),
    width: 22,
    format: (v: number) => (typeof v === "number" ? formatBRL(v) : "R$ 0,00"),
    alignment: "right",
  },
  {
    key: "platformFees",
    header: t("financial.platformFeeLabel"),
    width: 20,
    format: (v: number) => (typeof v === "number" ? formatBRL(v) : "R$ 0,00"),
    alignment: "right",
  },
  {
    key: "gatewayFees",
    header: t("financial.gatewayFee"),
    width: 24,
    format: (v: number) => (typeof v === "number" ? formatBRL(v) : "R$ 0,00"),
    alignment: "right",
  },
  {
    key: "refunds",
    header: t("financial.refundedAmount"),
    width: 22,
    format: (v: number) => (typeof v === "number" ? formatBRL(v) : "R$ 0,00"),
    alignment: "right",
  },
  {
    key: "netRevenue",
    header: t("financial.netRevenueFinal"),
    width: 22,
    format: (v: number) => (typeof v === "number" ? formatBRL(v) : "R$ 0,00"),
    alignment: "right",
  },
];

export default function ProducerFinancial() {
  const { t, i18n } = useTranslation("producerFinancial");
  const { user } = useAuth();
  const { activeContext } = useOrganization();
  const isOrgContext = activeContext.type === "organization" && activeContext.organizationId;
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = (searchParams.get("tab") as "fluxodecaixa" | "receber" | "pagar" | "conciliacao" | "bancos" | "saques" | "previsao" | "historico") || "conciliacao";
  const [periodLabel, setPeriodLabel] = useState<"30d" | "month" | "quarter" | "custom">("month");
  const [customStartDate, setCustomStartDate] = useState<string>("");
  const [customEndDate, setCustomEndDate] = useState<string>("");

  const handleTabChange = (value: string) => {
    const params = new URLSearchParams(searchParams);
    params.set("tab", value);
    setSearchParams(params, { replace: true });
  };

  const periodText = useMemo(() => {
    if (periodLabel === "30d") return t("financial.last30Days");
    if (periodLabel === "quarter") return t("financial.lastQuarter");
    if (periodLabel === "custom") {
      if (customStartDate && customEndDate) {
        return `${format(new Date(customStartDate + "T00:00:00"), "dd/MM/yyyy")} a ${format(new Date(customEndDate + "T00:00:00"), "dd/MM/yyyy")}`;
      }
      if (customStartDate) {
        return `${t("financial.startingFrom")} ${format(new Date(customStartDate + "T00:00:00"), "dd/MM/yyyy")}`;
      }
      if (customEndDate) {
        return `${t("financial.upTo")} ${format(new Date(customEndDate + "T00:00:00"), "dd/MM/yyyy")}`;
      }
      return t("financial.customPeriodLabel");
    }
    return t("financial.thisMonth");
  }, [periodLabel, customStartDate, customEndDate, t]);

  const { data: cashFlowData } = useQuery({
    queryKey: ["cash-flow", user?.id],
    queryFn: () => (user ? getCashFlowSummary(user.id) : null),
    staleTime: 30_000,
    enabled: !!user?.id,
  });

  const { data: reconciliationEvents = [], isLoading: isLoadingReconciliation } = useQuery({
    queryKey: ["event-reconciliation", user?.id, activeContext],
    queryFn: () => (user ? getEventReconciliation(user.id, isOrgContext ? activeContext.organizationId! : undefined) : []),
    staleTime: 30_000,
    enabled: !!user?.id,
  });

  const filteredReconciliationEvents: ReconciliationEvent[] = useMemo(() => {
    const now = new Date();
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const thirtyDaysAgo = new Date(now);
    thirtyDaysAgo.setDate(now.getDate() - 30);
    const ninetyDaysAgo = new Date(now);
    ninetyDaysAgo.setDate(now.getDate() - 90);

    return (reconciliationEvents as ReconciliationEvent[]).filter((event) => {
      if (!event.start_date) return true;
      const startDate = new Date(event.start_date);

      if (periodLabel === "30d") return startDate >= thirtyDaysAgo;
      if (periodLabel === "quarter") return startDate >= ninetyDaysAgo;
      if (periodLabel === "custom") {
        if (customStartDate) {
          const from = new Date(customStartDate + "T00:00:00");
          if (startDate < from) return false;
        }
        if (customEndDate) {
          const to = new Date(customEndDate + "T23:59:59");
          if (startDate > to) return false;
        }
        return true;
      }
      return startDate >= startOfMonth;
    });
  }, [reconciliationEvents, periodLabel, customStartDate, customEndDate]);

  const reconciliationSummary = useMemo(() => {
    const platformFees = filteredReconciliationEvents.reduce((s: number, e: ReconciliationEvent) => s + (e.platformFees || 0), 0);
    const ticketRevenue = filteredReconciliationEvents.reduce((s: number, e: ReconciliationEvent) => s + (e.ticketRevenue || 0), 0);
    const totalGross = ticketRevenue + platformFees;
    const netRevenue = filteredReconciliationEvents.reduce((s: number, e: ReconciliationEvent) => s + (e.netRevenue || 0), 0);
    const ticketsSold = filteredReconciliationEvents.reduce((s: number, e: ReconciliationEvent) => s + (e.ticketsSold || 0), 0);
    const refunds = filteredReconciliationEvents.reduce((s: number, e: ReconciliationEvent) => s + (e.refunds || 0), 0);

    return {
      totalGross,
      ticketRevenue,
      platformFees,
      netRevenue,
      ticketsSold,
      refunds,
    };
  }, [filteredReconciliationEvents]);

  const kpiCards = [
    {
      label: t("financial.totalGrossLabel"),
      value: isLoadingReconciliation ? "..." : formatBRL(reconciliationSummary.totalGross),
      hint: t("financial.totalGrossHint"),
      hero: false,
    },
    {
      label: t("financial.platformFeeLabel"),
      value: isLoadingReconciliation ? "..." : formatBRL(reconciliationSummary.platformFees),
      hint: t("financial.platformFeeHint"),
      hero: false,
    },
    {
      label: t("financial.totalRefundedLabel"),
      value: isLoadingReconciliation ? "..." : formatBRL(reconciliationSummary.refunds),
      hint: t("financial.totalRefundedHint"),
      hero: false,
    },
    {
      label: t("financial.netValueLabel"),
      value: isLoadingReconciliation ? "..." : formatBRL(reconciliationSummary.netRevenue),
      hint: t("financial.netValueHint"),
      hero: true,
    },
  ];

  const section = useMemo(() => {
    if (activeTab === "conciliacao") return "dashboard";
    if (activeTab === "saques" || activeTab === "previsao" || activeTab === "bancos" || activeTab === "historico") return "saques";
    return "fluxodecaixa";
  }, [activeTab]);

  if (!user) return null;

  return (
    <div className="w-full space-y-4">
      {/* 1. Dashboard / Conferência Section */}
      {section === "dashboard" && (
        <div className={panelPage}>
          <header className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h1 className={panelTitle}>{t("financial.dashboardTitle")}</h1>
              <p className="mt-1 text-xs sm:text-sm text-muted-foreground">
                {t("financial.dashboardDescription")}
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Select value={periodLabel} onValueChange={(val: "30d" | "month" | "quarter" | "custom") => setPeriodLabel(val)}>
                <SelectTrigger className="h-9 w-[170px] rounded-full border-border/60 bg-card hover:bg-secondary/40 text-xs font-medium shadow-none transition-colors px-3">
                  <div className="flex items-center gap-2 min-w-0">
                    <CalendarDays className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                    <SelectValue placeholder={t("financial.periodPlaceholder")} />
                  </div>
                </SelectTrigger>
                <SelectContent className="rounded-2xl border-border/60 shadow-lg">
                  <SelectItem value="month">{t("financial.thisMonth")}</SelectItem>
                  <SelectItem value="30d">{t("financial.last30Days")}</SelectItem>
                  <SelectItem value="quarter">{t("financial.thisQuarter")}</SelectItem>
                  <SelectItem value="custom">{t("financial.customPeriodOption")}</SelectItem>
                </SelectContent>
              </Select>
              <Button
                variant="outline"
                size="sm"
                className={cn("h-9 rounded-full px-4 text-xs font-medium gap-1.5 border-border/60 bg-card hover:bg-secondary/40 shadow-none transition-colors", panelPill)}
                onClick={async () => {
                  if (!filteredReconciliationEvents || filteredReconciliationEvents.length === 0) {
                    toast({ title: t("financial.noDataExportTitle"), description: t("financial.noDataExportDescription") });
                    return;
                  }
                  const periodSuffixMap: Record<string, string> = {
                    month: "este_mes",
                    "30d": "ultimos_30_dias",
                    quarter: "este_trimestre",
                    custom: "personalizado",
                  };
                  try {
                    await exportToXLSX(
                      filteredReconciliationEvents,
                      getReconciliationExcelColumns(t),
                      `conciliacao_financeira_eventos_${periodSuffixMap[periodLabel] || "periodo"}`
                    );
                    toast({ title: t("financial.excelGeneratedSuccess") });
                  } catch (error) {
                    toast({ title: t("financial.excelGeneratedErrorTitle"), description: t("financial.excelGeneratedErrorDescription"), variant: "destructive" });
                  }
                }}
              >
                <Download className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                <span>{t("financial.exportToExcel")}</span>
              </Button>
            </div>
          </header>

          {periodLabel === "custom" && (
            <div className="flex flex-wrap items-center gap-2.5 p-3 rounded-2xl border border-border/60 bg-card/60">
              <span className="text-xs text-muted-foreground font-medium">{t("financial.customPeriodLabel")}:</span>
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <span>{t("financial.from")}</span>
                <Input
                  type="date"
                  value={customStartDate}
                  onChange={(e) => setCustomStartDate(e.target.value)}
                  className="h-8 w-36 text-xs rounded-full border-border/50 bg-secondary/50 px-3 shadow-none focus-visible:bg-background"
                />
              </div>
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <span>{t("financial.to")}</span>
                <Input
                  type="date"
                  value={customEndDate}
                  onChange={(e) => setCustomEndDate(e.target.value)}
                  className="h-8 w-36 text-xs rounded-full border-border/50 bg-secondary/50 px-3 shadow-none focus-visible:bg-background"
                />
              </div>
              {(customStartDate || customEndDate) && (
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-8 px-3 rounded-full text-xs text-muted-foreground hover:text-foreground"
                  onClick={() => { setCustomStartDate(""); setCustomEndDate(""); }}
                >
                  {t("financial.clear")}
                </Button>
              )}
            </div>
          )}

          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            {kpiCards.map((kpi) => (
              <div
                key={kpi.label}
                className="relative h-full overflow-hidden rounded-[28px] border border-border/60 bg-card p-5 sm:p-6 transition-all"
              >
                <p className={panelEyebrow}>{kpi.label}</p>
                <p
                  className={cn(
                    "mt-2 font-display font-semibold tabular-nums tracking-tight",
                    kpi.hero ? "text-3xl sm:text-4xl text-emerald-600 dark:text-emerald-400" : "text-2xl sm:text-3xl text-foreground"
                  )}
                >
                  {kpi.value}
                </p>
                {kpi.hint && (
                  <p className="mt-1.5 text-xs text-muted-foreground line-clamp-1">{kpi.hint}</p>
                )}
              </div>
            ))}
          </div>

          <ProducerEventReconciliation
            producerId={user.id}
            events={filteredReconciliationEvents}
            isLoading={isLoadingReconciliation}
          />
        </div>
      )}

      {/* 2. Saques e Contas Section */}
      {section === "saques" && (
        <div className={`${panelPage} max-w-[1240px]`}>
          <header className="flex flex-col gap-2">
            <div>
              <h1 className={cn(panelTitle, "text-xl sm:text-2xl")}>{t("financial.withdrawalsTitle")}</h1>
              <p className="mt-1 text-xs text-muted-foreground sm:text-sm">Acompanhe cada real, do pagamento até a sua conta.</p>
            </div>
          </header>

          <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
            <div className="relative w-full overflow-x-auto pb-1 no-scrollbar touch-pan-x -mx-1 px-1">
              <TabsList className="flex h-auto w-max sm:w-fit justify-start rounded-full bg-secondary/70 p-1">
                <TabsTrigger
                  value="saques"
                  className={`${panelPill} h-11 sm:h-9 gap-2 px-4 text-xs sm:text-sm whitespace-nowrap min-w-[44px]`}
                >
                  <WalletCards className="h-4 w-4 shrink-0" />
                  <span>{t("financial.requestWithdrawalTab")}</span>
                </TabsTrigger>
                <TabsTrigger
                  value="previsao"
                  className={`${panelPill} h-11 sm:h-9 gap-2 px-4 text-xs sm:text-sm whitespace-nowrap min-w-[44px]`}
                >
                  <TrendingUp className="h-4 w-4 shrink-0" />
                  <span>Previsão de liberação</span>
                </TabsTrigger>
                <TabsTrigger
                  value="historico"
                  className={`${panelPill} h-11 sm:h-9 gap-2 px-4 text-xs sm:text-sm whitespace-nowrap min-w-[44px]`}
                >
                  <History className="h-4 w-4 shrink-0" />
                  <span>{t("financial.withdrawalHistoryTab")}</span>
                </TabsTrigger>
                <TabsTrigger
                  value="bancos"
                  className={`${panelPill} h-11 sm:h-9 gap-2 px-4 text-xs sm:text-sm whitespace-nowrap min-w-[44px]`}
                >
                  <Landmark className="h-4 w-4 shrink-0" />
                  <span>{t("financial.bankAccountsTab")}</span>
                </TabsTrigger>
              </TabsList>
            </div>

            {/* Sem card em volta: cada aba já desenha os próprios cartões */}
            <div className="mt-4">
              <TabsContent value="saques" className="m-0 outline-none ring-0 focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0">
                <ProducerWithdrawals producerId={user.id} organizationId={isOrgContext ? activeContext.organizationId! : undefined} view="form" />
              </TabsContent>
              <TabsContent value="previsao" className="m-0 outline-none ring-0 focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0">
                <ProducerWithdrawals producerId={user.id} organizationId={isOrgContext ? activeContext.organizationId! : undefined} view="forecast" />
              </TabsContent>
              <TabsContent value="historico" className="m-0 outline-none ring-0 focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0">
                <ProducerWithdrawals producerId={user.id} organizationId={isOrgContext ? activeContext.organizationId! : undefined} view="history" />
              </TabsContent>
              <TabsContent value="bancos" className="m-0 outline-none ring-0 focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0">
                <ProducerBankAccounts producerId={user.id} organizationId={isOrgContext ? activeContext.organizationId! : undefined} />
              </TabsContent>
            </div>
          </Tabs>
        </div>
      )}

      {/* 3. Fluxo de Caixa Section */}
      {section === "fluxodecaixa" && (
        <div className="space-y-5">
          <div className="rounded-2xl border border-border/70 bg-card p-4 md:p-5">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
              <div>
                <h1 className="text-2xl font-bold tracking-tight">{t("financial.cashFlowTitle")}</h1>
                <p className="mt-1 text-sm text-muted-foreground">
                  {t("financial.cashFlowDescription")}
                </p>
              </div>
              <div className="flex flex-col sm:flex-row sm:items-center gap-2 w-full lg:w-auto">
                <Button variant="outline" className="h-9 w-full sm:w-auto px-4 rounded-xl font-semibold shrink-0" onClick={() => {
                  const isEn = i18n.language?.startsWith("en");
                  const locale = isEn ? "en-US" : "pt-BR";
                  const filePrefix = isEn ? "cash-flow" : "fluxo-de-caixa";
                  const rows = cashFlowData?.items?.map((item: { due_date?: string; type?: string; description?: string; category?: string; amount?: number; status?: string }) => {
                    const statusLabel = isEn
                      ? (item.status === "paid" ? "Paid" : item.status === "pending" ? "Pending" : item.status)
                      : (item.status === "paid" ? "Pago" : item.status === "pending" ? "Pendente" : item.status);
                    return {
                      [isEn ? "Date" : "Data"]: item.due_date ? new Date(item.due_date).toLocaleDateString(locale) : "",
                      [isEn ? "Type" : "Tipo"]: item.type === "receivable" ? t("financial.revenue") : t("financial.expense"),
                      [isEn ? "Description" : "Descrição"]: item.description,
                      [isEn ? "Category" : "Categoria"]: item.category,
                      [isEn ? "Amount" : "Valor"]: item.amount,
                      [isEn ? "Status" : "Status"]: statusLabel,
                    };
                  }) || [];
                  if (rows.length === 0) {
                    toast({ title: t("financial.nothingToExportTitle"), description: t("financial.nothingToExportDescription") });
                    return;
                  }
                  const csv = [Object.keys(rows[0]).join(","), ...rows.map((r: Record<string, unknown>) => Object.values(r).map((v: unknown) => `"${String(v ?? "")}"`).join(","))].join("\n");
                  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
                  const link = document.createElement("a");
                  link.href = URL.createObjectURL(blob);
                  link.download = `${filePrefix}-${periodLabel}.csv`;
                  link.click();
                  URL.revokeObjectURL(link.href);
                }}>
                  {t("financial.export")}
                </Button>
              </div>
            </div>
          </div>

          <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
            <TabsList className="items-center text-muted-foreground flex w-full justify-start gap-8 rounded-none border-b border-border bg-transparent p-0 h-auto overflow-x-auto no-scrollbar">
              <TabsTrigger
                value="fluxodecaixa"
                className="inline-flex items-center justify-center whitespace-nowrap py-1.5 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 relative h-11 rounded-none border-b-2 border-transparent bg-transparent px-1 pb-3 pt-2 font-semibold text-muted-foreground shadow-none transition-none data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:text-foreground data-[state=active]:shadow-none gap-2"
              >
                <Activity className="h-4 w-4" />
                <span>{t("financial.cashFlowTab")}</span>
              </TabsTrigger>
              <TabsTrigger
                value="receber"
                className="inline-flex items-center justify-center whitespace-nowrap py-1.5 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 relative h-11 rounded-none border-b-2 border-transparent bg-transparent px-1 pb-3 pt-2 font-semibold text-muted-foreground shadow-none transition-none data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:text-foreground data-[state=active]:shadow-none gap-2"
              >
                <TrendingUp className="h-4 w-4" />
                <span>{t("financial.accountsReceivableTab")}</span>
              </TabsTrigger>
              <TabsTrigger
                value="pagar"
                className="inline-flex items-center justify-center whitespace-nowrap py-1.5 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 relative h-11 rounded-none border-b-2 border-transparent bg-transparent px-1 pb-3 pt-2 font-semibold text-muted-foreground shadow-none transition-none data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:text-foreground data-[state=active]:shadow-none gap-2"
              >
                <TrendingDown className="h-4 w-4" />
                <span>{t("financial.accountsPayableTab")}</span>
              </TabsTrigger>
            </TabsList>

            <div className="mt-4 rounded-2xl border border-border/70 bg-card p-3 md:p-4">
              <TabsContent value="fluxodecaixa" className="m-0">
                <ProducerCashFlow producerId={user.id} />
              </TabsContent>
              <TabsContent value="receber" className="m-0">
                <ProducerAccountsReceivable producerId={user.id} />
              </TabsContent>
              <TabsContent value="pagar" className="m-0">
                <ProducerAccountsPayable producerId={user.id} />
              </TabsContent>
            </div>
          </Tabs>
        </div>
      )}
    </div>
  );
}
